import React, { useMemo } from 'react';
import {
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
} from '@mui/material';
import { FilterColumn } from '../../../../types/filters';

interface FilterDialogProps {
  open: boolean;
  onClose: () => void;
  selectedColumn: string;
  filterValue: string;
  onColumnChange: (value: string) => void;
  onValueChange: (value: string) => void;
  onAddFilter: () => void;
  columns: FilterColumn[];
  editingFilterId?: string | null;
  /**
   * Opt-in: tela cheia, campos 16px/48px e botões 48px. É um boolean EXPLÍCITO decidido pelo chamador
   * (o módulo já classifica "celular" uma vez); o diálogo não duplica heurística de viewport.
   */
  fullScreen?: boolean;
}

export const FilterDialog: React.FC<FilterDialogProps> = ({
  open,
  onClose,
  selectedColumn,
  filterValue,
  onColumnChange,
  onValueChange,
  onAddFilter,
  columns,
  editingFilterId,
  fullScreen = false,
}) => {
  // Fonte >= 16px evita o zoom automático do Safari iOS; altura de toque confortável.
  const touchFieldSx = fullScreen
    ? {
        '& .MuiInputBase-root, & .MuiInputBase-root.MuiInputBase-sizeSmall': { minHeight: 48 },
        '& .MuiInputBase-input': { fontSize: 16 },
      }
    : undefined;
  const selectedDef = useMemo(
    () => columns.find((c) => c.value === selectedColumn),
    [columns, selectedColumn],
  );
  const canAddFilter =
    selectedColumn && (selectedDef?.type === 'date' ? !!filterValue : filterValue.trim());
  const isEditing = !!editingFilterId;
  const selectOptions = selectedDef?.options ?? [];
  const selectedColumnOption = columns.find((column) => column.value === selectedColumn) ?? null;
  const selectedOption =
    selectOptions.find((opt) => String(opt.value) === String(filterValue)) ?? null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
      <DialogTitle>{isEditing ? 'Editar Filtro' : 'Adicionar Filtro'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1, ...(touchFieldSx ?? {}) }}>
          <Autocomplete
            options={columns}
            value={selectedColumnOption}
            onChange={(_event, option) => {
              onColumnChange(option ? option.value : '');
              onValueChange('');
            }}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            noOptionsText="Nenhuma coluna encontrada"
            fullWidth
            renderInput={(params) => (
              <TextField {...params} label="Selecionar Coluna" placeholder="Pesquisar..." />
            )}
          />

          {selectedDef?.type === 'select' && selectOptions.length > 0 ? (
            <Autocomplete
              options={selectOptions}
              value={selectedOption}
              onChange={(_event, option) => onValueChange(option ? String(option.value) : '')}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => String(option.value) === String(value.value)}
              noOptionsText="Nenhum item encontrado"
              fullWidth
              renderInput={(params) => (
                <TextField {...params} label="Valor" placeholder="Pesquisar..." />
              )}
            />
          ) : selectedDef?.type === 'date' ? (
            <TextField
              type="date"
              label="Data"
              value={filterValue}
              onChange={(e) => onValueChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          ) : selectedDef?.type === 'number' ? (
            <TextField
              type="number"
              label="Valor do Filtro"
              value={filterValue}
              onChange={(e) => onValueChange(e.target.value)}
              fullWidth
            />
          ) : (
            <TextField
              label="Valor do Filtro"
              value={filterValue}
              onChange={(e) => onValueChange(e.target.value)}
              placeholder="Digite o valor para filtrar..."
              fullWidth
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions
        sx={
          fullScreen
            ? {
                px: 1.5,
                pt: 1,
                pb: 'calc(8px + env(safe-area-inset-bottom, 0px))',
                borderTop: '1px solid',
                borderColor: 'divider',
                gap: 1,
                '& .MuiButton-root': { minHeight: 48, flex: 1 },
              }
            : undefined
        }
      >
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={onAddFilter} variant="contained" disabled={!canAddFilter}>
          {isEditing ? 'Salvar Alterações' : 'Adicionar Filtro'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
